import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, X, Save, Eye, EyeOff } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { SERVICE_COLORS, SERVICE_ICONS, getServiceColor, getServiceIcon } from '../utils/servicePresentation';

const emptyForm = {
  title: '',
  titleEn: '',
  description: '',
  descriptionEn: '',
  category: '',
  iconKey: 'layers',
  colorKey: 'blue',
  featuresText: '',
  active: true,
  order: 0,
};

const toForm = (service) => ({
  title: service?.title || '',
  titleEn: service?.titleEn || '',
  description: service?.description || '',
  descriptionEn: service?.descriptionEn || '',
  category: service?.category || '',
  iconKey: service?.iconKey || 'layers',
  colorKey: service?.colorKey || 'blue',
  featuresText: Array.isArray(service?.features) ? service.features.join('\n') : '',
  active: service?.active !== false,
  order: Number(service?.order || 0),
});

const ServicesAdmin = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const categories = useMemo(() => [...new Set(services.map(s => s.category).filter(Boolean))], [services]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/services?all=true');
      setServices(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('تعذر تحميل الخدمات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadServices(); }, []);

  const openCreate = () => {
    setEditing('new');
    setForm({ ...emptyForm, order: services.length + 1 });
  };

  const openEdit = (service) => {
    setEditing(service._id);
    setForm(toForm(service));
  };

  const closeEditor = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const saveService = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.category.trim()) {
      toast.error('اسم الخدمة والوصف والتصنيف مطلوبة');
      return;
    }

    const payload = {
      title: form.title.trim(),
      titleEn: form.titleEn.trim(),
      description: form.description.trim(),
      descriptionEn: form.descriptionEn.trim(),
      category: form.category.trim(),
      iconKey: form.iconKey,
      colorKey: form.colorKey,
      features: form.featuresText.split('\n').map(x => x.trim()).filter(Boolean),
      active: Boolean(form.active),
      order: Number(form.order || 0),
    };

    try {
      if (editing === 'new') {
        const { data } = await api.post('/services', payload);
        setServices(prev => [...prev, data].sort((a, b) => (a.order || 0) - (b.order || 0)));
        toast.success('تمت إضافة الخدمة');
      } else {
        const { data } = await api.put(`/services/${editing}`, payload);
        setServices(prev => prev.map(x => x._id === editing ? data : x).sort((a, b) => (a.order || 0) - (b.order || 0)));
        toast.success('تم حفظ تعديلات الخدمة');
      }
      closeEditor();
    } catch (error) {
      toast.error(error.response?.data?.message || 'تعذر حفظ الخدمة');
    }
  };

  const deleteService = async (service) => {
    if (!window.confirm(`هل تريد حذف خدمة "${service.title}"؟`)) return;
    try {
      await api.delete(`/services/${service._id}`);
      setServices(prev => prev.filter(x => x._id !== service._id));
      toast.success('تم حذف الخدمة');
    } catch (error) {
      toast.error('تعذر حذف الخدمة');
    }
  };

  const toggleActive = async (service) => {
    try {
      const { data } = await api.put(`/services/${service._id}`, { active: !service.active });
      setServices(prev => prev.map(x => x._id === service._id ? data : x));
    } catch (error) {
      toast.error('تعذر تغيير حالة الخدمة');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">الخدمات</h1>
          <p className="text-gray-500 text-sm mt-1">إضافة وتعديل وحذف خدمات الموقع الخارجي وتحديد الأيقونة المناسبة لكل نشاط.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary inline-flex items-center justify-center gap-2">
          <Plus size={18} /> إضافة خدمة جديدة
        </button>
      </div>

      {loading ? (
        <div className="glass p-10 text-center text-gray-400">جارٍ تحميل الخدمات...</div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {services.map((service) => {
            const Icon = getServiceIcon(service.iconKey);
            const color = getServiceColor(service.colorKey);
            return (
              <div key={service._id} className="glass p-6 group">
                <div className="flex items-start justify-between gap-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${color.gradient} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <button type="button" onClick={() => toggleActive(service)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs ${service.active ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-gray-500/10 border-gray-500/20 text-gray-400'}`}>
                    {service.active ? <Eye size={13} /> : <EyeOff size={13} />} {service.active ? 'ظاهر بالموقع' : 'مخفي'}
                  </button>
                </div>
                <div className="mt-5">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-white">{service.title}</h3>
                    <span className={`px-2 py-0.5 rounded-md border text-[11px] ${color.badge}`}>{service.category}</span>
                  </div>
                  <p className="text-gray-400 text-sm leading-6 min-h-[48px]">{service.description}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                    <span>الترتيب: {service.order || 0}</span>
                    <span>{Array.isArray(service.features) ? service.features.length : 0} مميزات</span>
                  </div>
                </div>
                <div className="flex gap-2 mt-5 pt-4 border-t border-white/10">
                  <button type="button" onClick={() => openEdit(service)} className="flex-1 px-3 py-2 rounded-lg bg-orvix-primary/15 text-orvix-primary hover:bg-orvix-primary/25 transition-all inline-flex items-center justify-center gap-2 text-sm">
                    <Pencil size={15} /> تعديل
                  </button>
                  <button type="button" onClick={() => deleteService(service)} className="px-3 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all inline-flex items-center gap-2 text-sm">
                    <Trash2 size={15} /> حذف
                  </button>
                </div>
              </div>
            );
          })}
          {!services.length && <div className="glass p-10 text-center text-gray-400 md:col-span-2 xl:col-span-3">لا توجد خدمات. أضيفي أول خدمة من الزر بالأعلى.</div>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeEditor}>
          <div className="glass w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 sm:p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={closeEditor} className="absolute left-5 top-5 text-gray-400 hover:text-white"><X size={22} /></button>
            <h2 className="text-xl font-bold text-white mb-7">{editing === 'new' ? 'إضافة خدمة جديدة' : 'تعديل الخدمة'}</h2>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-gray-300 text-sm mb-2">اسم الخدمة بالعربي *</label>
                <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">اسم الخدمة بالإنجليزي</label>
                <input dir="ltr" value={form.titleEn} onChange={(e) => setForm({...form, titleEn: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-300 text-sm mb-2">الوصف بالعربي *</label>
                <textarea rows="3" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-300 text-sm mb-2">الوصف بالإنجليزي</label>
                <textarea dir="ltr" rows="2" value={form.descriptionEn} onChange={(e) => setForm({...form, descriptionEn: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none resize-none" />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">التصنيف *</label>
                <input list="service-categories" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} placeholder="مثال: طبية، مقاولات، محاسبة" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
                <datalist id="service-categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">الترتيب</label>
                <input type="number" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-300 text-sm mb-3">الأيقونة</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(SERVICE_ICONS).map(([key, item]) => {
                    const Icon = item.icon;
                    const selected = form.iconKey === key;
                    return (
                      <button type="button" key={key} onClick={() => setForm({...form, iconKey: key})} className={`p-3 rounded-xl border flex items-center gap-2 text-sm transition-all ${selected ? 'border-orvix-green bg-orvix-green/10 text-white' : 'border-white/10 bg-white/5 text-gray-400 hover:text-white'}`}>
                        <Icon size={20} className={selected ? 'text-orvix-green' : ''} /> {item.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2">مثال: الطبية بأيقونة سماعة طبيب، والمقاولات بأيقونة مبنى.</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-300 text-sm mb-3">لون الأيقونة</label>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(SERVICE_COLORS).map(([key, item]) => (
                    <button type="button" key={key} onClick={() => setForm({...form, colorKey: key})} className={`px-4 py-2 rounded-xl border text-sm ${form.colorKey === key ? 'border-white/40 bg-white/10 text-white' : 'border-white/10 text-gray-400'}`}>
                      <span className={`inline-block w-3 h-3 rounded-full bg-gradient-to-br ${item.gradient} ml-2`} /> {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-300 text-sm mb-2">المميزات</label>
                <textarea rows="5" value={form.featuresText} onChange={(e) => setForm({...form, featuresText: e.target.value})} placeholder={'كل ميزة في سطر مستقل\nإدارة المرضى\nالمواعيد الإلكترونية'} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none resize-none" />
              </div>

              <label className="md:col-span-2 flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} className="w-4 h-4 accent-emerald-500" />
                <div><div className="text-white text-sm font-medium">إظهار الخدمة في الموقع الخارجي</div><div className="text-xs text-gray-500 mt-1">يمكن إخفاء الخدمة مؤقتًا بدون حذفها.</div></div>
              </label>
            </div>

            <div className="flex gap-3 justify-end mt-7 pt-5 border-t border-white/10">
              <button type="button" onClick={closeEditor} className="px-5 py-2.5 rounded-xl bg-white/5 text-gray-300 hover:bg-white/10">إلغاء</button>
              <button type="button" onClick={saveService} className="btn-primary inline-flex items-center gap-2"><Save size={17} /> حفظ الخدمة</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesAdmin;
